import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { PortalI18nProvider, usePortalI18n } from '../../context/PortalI18nContext';
import { Button } from '../../components/ui/button';
import { Building2, FileCheck2, Files, LayoutDashboard, LogOut, Menu, X, Languages } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inner layout — wrapped by PortalI18nProvider below. Splitting allows us
// to call usePortalI18n() inside the header without the chicken-and-egg of
// provider mounting in the same component.
const PortalLayoutInner = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = usePortalI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [division, setDivision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/me`, { withCredentials: true });
        setDivision(res.data.division || null);
      } catch (e) {
        if (e?.response?.status === 403) {
          navigate('/', { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    if (user && user.role && user.role !== 'division_secretary' && !user.is_super_admin) {
      // non-portal user — send to admin landing
    }
  }, [user]);

  // Nav labels are translated at render time so toggling language updates
  // them instantly without re-mounting.
  const navItems = [
    { to: '/portal', icon: LayoutDashboard, key: 'nav.dashboard', testkey: 'dashboard', end: true },
    { to: '/portal/claims', icon: FileCheck2, key: 'nav.myClaims', testkey: 'my-claims' },
    { to: '/portal/kyc', icon: Files, key: 'nav.kyc', testkey: 'kyc-documents' },
  ];

  const toggleLang = () => {
    const next = lang === 'en' ? 'hi' : 'en';
    setLang(next);
    // Persist for the AI prompt — fire-and-forget; localStorage already
    // makes the UI switch instant so we don't await this.
    axios.put(`${API}/portal/preferences/language`, { lang: next }, { withCredentials: true })
      .catch(() => { /* non-fatal */ });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-amber-50/30">
      <header className="border-b bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost" size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="portal-menu-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div className="h-9 w-9 rounded-lg bg-emerald-700 text-white grid place-items-center flex-shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold leading-tight truncate">
                {division?.name || t('layout.divisionPortal')}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('layout.brandSuffix')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language toggle — instant switch, persisted in localStorage. */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLang}
              title={t('lang.tooltip')}
              data-testid="portal-lang-toggle"
              className="gap-1.5"
            >
              <Languages className="h-3.5 w-3.5" />
              <span className="font-semibold text-xs">
                {lang === 'en' ? 'हिन्दी' : 'English'}
              </span>
            </Button>
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium leading-tight">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email}</div>
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={() => { logout(); navigate('/login'); }}
              data-testid="portal-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('layout.logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside
          className={`${mobileOpen ? 'block' : 'hidden'} lg:block`}
          data-testid="portal-sidebar"
        >
          <nav className="space-y-1">
            {navItems.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition ${
                      isActive
                        ? 'bg-emerald-700 text-white shadow-sm'
                        : 'text-foreground/70 hover:bg-muted hover:text-foreground'
                    }`
                  }
                  data-testid={`portal-nav-${it.testkey}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t(it.key)}</span>
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0">
          {loading ? (
            <div className="text-sm text-muted-foreground">{t('layout.loadingPortal')}</div>
          ) : !division ? (
            <div className="text-sm text-rose-600">{t('layout.noDivisionLink')}</div>
          ) : (
            <Outlet context={{ division, refreshDivision: async () => {
              const r = await axios.get(`${API}/portal/me`, { withCredentials: true });
              setDivision(r.data.division || null);
            }, route: location.pathname }} />
          )}
        </main>
      </div>
    </div>
  );
};

const PortalLayout = () => (
  <PortalI18nProvider>
    <PortalLayoutInner />
  </PortalI18nProvider>
);

export default PortalLayout;
